import React, { useEffect, useRef } from 'react';
import { select } from 'd3-selection';
import { colors } from 'constants/colors';

import {
  DEFAULT_GRAPH_DIMENSIONS,
  TIMELINE_HORIZONTAL_LINE_ID,
  TIMELINE_GREEN_TOOLTIP_LINE_ID,
  TOOLTIP_LINE_STROKE,
  XCoordinates,
  TimeLineBubbleIds,
  TIMELINE_CENTER_LINE_ID,
} from '../constants';
import { TimelineBubbleType } from '../types';

type Props = {
  bubbles: TimelineBubbleType[];
};

const CenterLine: React.FC<Props> = props => {
  const centerLineElementRef = useRef(null);

  const { bubbles } = props;

  useEffect(() => {
    if (!centerLineElementRef.current) return;

    const node = centerLineElementRef.current;
    const centerY = DEFAULT_GRAPH_DIMENSIONS.height / 2;
    const centerX = DEFAULT_GRAPH_DIMENSIONS.width / 2;

    const svgG = select(node);

    // Draw base center line
    svgG
      .append('line')
      .attr('x1', 0)
      .attr('y1', centerY)
      .attr(
        'x2',
        DEFAULT_GRAPH_DIMENSIONS.width - DEFAULT_GRAPH_DIMENSIONS.padding,
      )
      .attr('y2', centerY)
      .style('stroke', colors.primaryColor500)
      .style('stroke-width', TOOLTIP_LINE_STROKE * 2);

    // Draw center line for hover (hidden by default)
    svgG
      .append('line')
      .attr('id', TIMELINE_CENTER_LINE_ID)
      .attr('x1', centerX)
      .attr('y1', 0)
      .attr('x2', centerX)
      .attr('y2', 65)
      .attr('stroke', colors.primaryColor200)
      .attr('stroke-width', TOOLTIP_LINE_STROKE)
      .attr('display', 'none');

    // Create lines from purple bubbles: up, then to center, then up
    const purpleBubbles = bubbles.filter(
      b =>
        b.id === TimeLineBubbleIds.PURPLE_1 ||
        b.id === TimeLineBubbleIds.PURPLE_2,
    );

    const bubbleRadius = 12;

    purpleBubbles.forEach((bubble, index) => {
      const startX = XCoordinates[bubble.position];
      const segmentLength = Math.abs(startX - centerX); // Distance from bubble to center

      const verticalStartY = centerY - bubbleRadius;

      svgG
        .append('polyline')
        .attr('id', `${TIMELINE_HORIZONTAL_LINE_ID}-${index}`)
        .attr(
          'points',
          `${startX}, ${verticalStartY}, ${startX}, ${verticalStartY -
            segmentLength}, ${centerX}, ${verticalStartY -
            segmentLength}, ${centerX}, ${verticalStartY - segmentLength * 2}`,
        )
        .attr('stroke', colors.primaryColor300)
        .attr('fill', 'none')
        .attr('stroke-width', TOOLTIP_LINE_STROKE * 2)
        .attr('display', 'none');
    });

    // Create horizontal line from right bubble to center
    svgG
      .append('line')
      .attr('id', 'right-to-center-line')
      .attr('x1', XCoordinates.right)
      .attr('y1', centerY)
      .attr('x2', centerX)
      .attr('y2', centerY)
      .attr('stroke', 'white')
      .attr('stroke-width', 1)
      .attr('display', 'none');

    const greenBuble = bubbles.find(b => b.id === TimeLineBubbleIds.GREEN);
    if (greenBuble) {
      if (greenBuble.position === 'middle') {
        svgG
          .append('line')
          .attr('id', TIMELINE_GREEN_TOOLTIP_LINE_ID)
          .attr('x1', XCoordinates.middle)
          .attr('y1', centerY)
          .attr('x2', XCoordinates.middle)
          .attr('y2', centerY + 240)
          .attr('stroke', colors.supportive500)
          .attr('stroke-width', TOOLTIP_LINE_STROKE * 2)
          .attr('display', 'none');
      } else if (greenBuble.position === 'right') {
        svgG
          .append('polyline')
          .attr('id', TIMELINE_GREEN_TOOLTIP_LINE_ID)
          .attr('stroke', colors.supportive500)
          .attr('fill', 'none')
          .attr(
            'points',
            `${XCoordinates.right}, ${centerY},
            ${XCoordinates.right}, ${centerY + 75},
            ${centerX - 1}, ${centerY + 75},
            ${centerX - 1}, ${centerY + 150}`,
          )
          .attr('stroke-width', TOOLTIP_LINE_STROKE * 2)
          .attr('display', 'none');
      }
    }

    // eslint-disable-next-line
  }, [bubbles]);

  return <g ref={centerLineElementRef} />;
};

export default CenterLine;
